import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CRUDPage from './CRUDPage';
import submenuLegos from './menus/submenu-legos';
import NiceDate from '../components/common/NiceDate';
import LegoContextSummary from '../components/lego/LegoContextSummary';
import _ from 'lodash';
import ImageClusterPreview from '../components/images/ImageClusterPreview';
import { IconButton } from '../components/common/IconButton';
import ImageTopSimilarityFinder from '../components/images/ImageTopSimilarityFinder';
import { LegoFiltersBar } from '../components/common/LegoFiltersBar';
import MultiButtonSwitch from '../components/common/editors/MultiButtonSwitch';
import ModalMergeImageClusters from '../components/images/ModalMergeImageClusters'; // NEW IMPORT
import ModalMoveImageClusterImages from '../components/images/ModalMoveImageClusterImages';
import ModalFindDuplicateClusters from '../components/images/ModalFindDuplicateClusters';
import ModalClusterReferences from '../components/images/ImageClusterLegoReferences';
import ModalLinkedClusters from '../components/images/ModalLinkedClusters';
import MultiClusterScatterModal from '../components/embedding-explorer/MultiClusterScatterModal';
import { SwitchInput } from '../components/common/SwitchInput';
import ModelGenerationsTimeline from '../components/lego/ModelGenerationsTimeline';
import BadgeColorHash from '../components/common/BadgeColorHash';


dayjs.extend(relativeTime);


class ImgClustersManager extends CRUDPage {
  constructor(props) {
    super(props, 'services/data/image-clusters');
    this.submenu = submenuLegos;

    this.state.columnSort = { sorter: (c) => this.clusterMatchingVehicles(c).length, order: 'desc' }
    this.state.previewSize = '50';
    this.state.sortByYear = false;
    this.canDelete = false;
    this.canEdit = false;
    this.canCreate = false;
    this.timelineRef = React.createRef();
    this.timelineHighlightTd = null;

    // setTimeout(() => {
    //   this.openTidyClusters();
    // }, 300);
  }

  getFacetFiltersComponent() {
    const fields = ['context', 'countries', '_id'];
    const loadedCount = (this.state.objects || []).length;
    return <>
      <LegoFiltersBar fields={fields} onChange={this.onFiltersChange.bind(this)} filter={this.state.facetFilters} />
      <span className={'ml-4 zoom-90'}>
        Thumbnail size: <MultiButtonSwitch value={this.state.previewSize} options={['50', '100', '200']} onChange={size => this.setState({ previewSize: size })} />
      </span>
      <IconButton className={'ml-3'} icon={'auto_awesome'} small fill level={'violeta'}
        title={'Use embeddings to detect duplicate clusters and isolated images that belong to another cluster'}
        disabled={!loadedCount}
        onClick={() => this.openTidyClusters()}>
        Find duplicates
      </IconButton>
    </>
  }

  // LegoFiltersBar's _id filter calls page.loadAndEdit() when a single id is entered
  // (it only sets the _id filter for 2+ ids). This page is read-only, so instead of
  // opening an editor, filter by that cluster id.
  loadAndEdit(id) {
    this.onFiltersChange({ ...this.state.facetFilters, _id: { $in: [id] } });
  }

  openTidyClusters() {
    const clusters = this.filterAndSortObjects();
    const clusterIds = _.map(clusters, '_id');
    if (!clusterIds.length) {
      return;
    }

    this.runAsync(async () => {
      // Thresholds are on the [0,1] unit similarity scale ((cos+1)/2): 0.9==0.8cos, 0.1==0.2cos slack, 0.94==0.88cos.
      const result = await this.service('services/data/fusebox-tidy-clusters').find({ query: { 
        clusterIds, 
        minPairSimilarity: 0.9, 
        minPairMedianSimilarity: 0.85, 
        minCentroidSimilarity: 0.9, 
        smallClusterCrossModelMinSimilarity: 0.96,
        smallClusterMaxImages: 2,
        smallClusterSimilarSearchLimit: 10,
        crossModelMinTargetImages: 4,
      } });
      this.openModal(<ModalFindDuplicateClusters
        result={result}
        onSave={() => {
          this.closeModal();
          this.fetchObjects();
          this.setState({ selection: [] });
        }}
        onCancel={() => this.closeModal()} />,
      { title: 'Tidy clusters', fullScreen: true });
    }, 'Analyzing clusters with embeddings...');
  }

    
  showStats() {
    return <ModelGenerationsTimeline ref={this.timelineRef} context={this.state.facetFilters?.context} />;
  }

  // Hovering rows fires mouseenter/leave very often. setState here would re-render the
  // whole CRUD table (potentially thousands of image thumbnails) on every crossing.
  // Instead we push overlay data into ModelGenerationsTimeline via ref, and toggle the
  // row highlight with classList — only the timeline bar re-renders.
  setTimelineOverlay(cluster, td) {
    if (this.timelineHighlightTd && this.timelineHighlightTd !== td) {
      this.timelineHighlightTd.classList.remove('bg-light-primary');
      this.timelineHighlightTd = null;
    }

    if (!cluster) {
      this.timelineRef.current?.setOverlayVehicles(null);
      if (td) {
        td.classList.remove('bg-light-primary');
        if (this.timelineHighlightTd === td) this.timelineHighlightTd = null;
      }
      return;
    }

    const vehicles = cluster.images.map(i => ({
      year: i.vehicle.year,
      country: i.vehicle.countryCode,
    }));
    this.timelineRef.current?.setOverlayVehicles(vehicles);
    if (td) {
      td.classList.add('bg-light-primary');
      this.timelineHighlightTd = td;
    }
  }

  buildObjectQuery() {
    let query = super.buildObjectQuery();


    let contextMake = query['context.make'];
    if (contextMake) {
      query['context.modelId'] = { $regex: `^${contextMake}-` };
      delete query['context.make'];
      query.$limit = 5000;
    }

    let year = query['context.year'];
    if (year) {
      query.images = query.images || { $elemMatch: {} };
      query.images.$elemMatch['vehicle.year'] = parseInt(year.toString());
      delete query['context.year'];
    }

    let countries = query['countries'];
    if (countries) {
      query.images = query.images || { $elemMatch: {} };
      query.images.$elemMatch['vehicle.countryCode'] = countries;
      query['images.vehicle.countryCode'] = countries;
      delete query['countries'];
    }

    console.log("QUERY", query);
    return query;
  }

  getColumnsDefinition(objects) {
    return [
      { content: 'Last update', className: 'text-center', sorter: 'updatedAt' },
      ...this.getRankColumnHeaders(),
      { content: '', className: 'text-center', sorter: 'state' },
      { content: 'Context', className: 'text-center', sorter: (c) => this.clusterYears(c).median },
      { content: 'Imgs', className: 'text-center', sorter: (c) => -this.clusterMatchingVehicles(c).length },
      {
        content: <span>
          <span className='align-middle'>Data</span>
          <SwitchInput className={'zoom-75 ml-3'} value={this.state.sortByYear} onChange={() => this.setState({ sortByYear: !this.state.sortByYear })}>
            Sort thumbs by year
          </SwitchInput>
        </span>, className: 'text-center', sorter: (c) => -this.clusterMatchingVehicles(c).length
      }
    ];
  }

  clusterMatchingVehicles(cluster) {
    let year = this.state.facetFilters?.context?.year;
    let countries = this.state.facetFilters?.countries;


    return cluster.images.filter(i => (!year || i.vehicle.year === year) && (!countries || countries?.includes(i.vehicle.countryCode)));
  }

  clusterYears(cluster) {
    let years = _.map(cluster.images, 'vehicle.year');
    let min = Math.min(...years);
    let max = Math.max(...years);
    let median = years.sort()[Math.floor(years.length / 2)];
    return { min, median, max };
  }

  getObjectColumns(cluster) {
    let {
      _id, createdBy, createdAt, updatedBy, updatedAt, clusterType, images, context, totalImages
    } = cluster;


    let year = this.state.facetFilters?.context?.year;
    let countries = this.state.facetFilters?.countries;


    let matching = this.clusterMatchingVehicles(cluster)

    let maxImages = (this.state.previewSize === '50' ? 17 : this.state.previewSize === '100' ? 8 : this.state.previewSize === '200' ? 4 : 4);

    let { min, median, max } = this.clusterYears(cluster);

    return [    

      <td key={'id'}>
        <NiceDate t={updatedAt} />
        <br/>
        <BadgeColorHash id={_id} className='zoom-75 mt-1'/>
        <br/>
      </td>,

      ... this.getRankColumns(cluster),

      <td key={'feedbackType'} className={'pl-3 text-center'}>
        <IconButton level="violeta" icon={'image_search'} className={'zoom-90'}
        title={"Find images most similar to this cluster's first image"}
        onClick={() => this.openSimilarImagesModal(cluster)} /><br/>
        <IconButton level="violeta" icon={'hub'} className={'zoom-90'}
        title={'Show clusters linked/rejected to this one (image_cluster_links)'}
        onClick={() => this.openLinkedClustersModal(cluster)} />
      </td>,

      <td key={'context'}
      onMouseEnter={(e) => this.setTimelineOverlay(cluster, e.currentTarget)}
      onMouseLeave={(e) => this.setTimelineOverlay(null, e.currentTarget)}>
        <LegoContextSummary context={context || {}} />
        <div>
          <div className={'text-info'}>~ {median}</div>
          <span className={'text-secondary small'}>{min} - {max}</span>
        </div>
      </td>,


      <td key={'total'} className={'text-center'}>
        {matching.length !== totalImages ? <span className={'text-primary'}>{matching.length} / </span> : null}
        {totalImages}
      </td>,

      <td key={'data'}>
        <ImageClusterPreview size={this.state.previewSize} sortByYear={this.state.sortByYear} clusterId={_id} highlightYear={year} highlightCountries={countries} maxImages={maxImages} preloadedCluster={cluster} />      
      </td>
    ];
  }

  openLinkedClustersModal(cluster) {
    this.openModal(<ModalLinkedClusters clusterId={String(cluster._id)} />,
      { title: <span>Clusters linked to <BadgeColorHash id={cluster._id} /></span>, fullScreenFixed: true });
  }

  openSimilarImagesModal(cluster) {
    const firstImageId = cluster.images?.[0]?.id;
    if (!firstImageId) {
      return;
    }
    this.openModal(<ImageTopSimilarityFinder imgId={firstImageId} showControls={true} />, { title: `Images similar to cluster's first image`, fullScreen: true });
  }

  // getObjectEditorComponent(obj, defaultOnSaveCbk, defaultOnCancelCbk) {
    // return <DataIssueEditor obj={obj} onSave={defaultOnSaveCbk} onCancel={defaultOnCancelCbk} />
  // }

  openMergeClustersModal(clusters) {
    clusters = _.sortBy(clusters, c => c.images?.length);

    this.openModal(<ModalMergeImageClusters clusters={clusters}
      onSave={() => {
        this.closeModal();
        this.fetchObjects();
        this.setState({ selection: [] });
      }}
      onCancel={() => this.closeModal()} />, { title: 'Merge Image Clusters' });
  }

  openMoveClusterModal(cluster) {
    const allImageIds = _.map(cluster.images, img => img.id);
    this.openModal(<ModalMoveImageClusterImages
      sourceCluster={cluster}
      selectedImageIds={allImageIds}
      onSave={() => {
        this.closeModal();
        this.fetchObjects();
        this.setState({ selection: [] });
      }}
      onCancel={() => this.closeModal()} />,
    { title: 'Move cluster to another destination', fullScreen: true, noPadding: true });
  }

  openFindReferencesModal(clusters) {
    const clusterIds = _.map(clusters, '_id');
    this.openModal(<ModalClusterReferences clusterIds={clusterIds} />,
      { title: 'Fuseboxes and templates referencing the selected clusters' });
  }

  openClusterScatterModal(clusters) {
    const clusterIds = _.map(clusters, '_id').map(String);
    this.openModal(<MultiClusterScatterModal clusterIds={clusterIds} />,
      { title: <span>Embedding scatter of {clusterIds.length} clusters</span>, fullScreenFixed: true, oPadding: true });
  }

  getSelectionButtons(selection) {
    if (!selection?.length) {
      return super.getSelectionButtons(selection);
    }

    const findReferencesBtn = <IconButton icon={'manage_search'}
      title={'Find fuseboxes and templates that reference the selected clusters'}
      onClick={() => this.openFindReferencesModal(selection)}>      
    </IconButton>;

    // Plot the selected clusters' embeddings together (2 clusters → discriminant/Fisher separation view).
    const scatterBtn = selection.length >= 2 ? <IconButton icon={'scatter_plot'} level={'violeta'}
      title={'Plot the selected clusters\' image embeddings together (PCA / discriminant separation)'}
      onClick={() => this.openClusterScatterModal(selection)}>      
    </IconButton> : null;

    if (selection.length === 2) {
      return <>
        {super.getSelectionButtons(selection)}
        <IconButton icon={'merge_type'} title={'Merge clusters'} onClick={() => this.openMergeClustersModal(selection)}>
   
        </IconButton>
        {scatterBtn}
        {findReferencesBtn}
      </>
    }
    if (selection.length === 1) {
      const cluster = selection[0];
      const hasImages = cluster.images?.length > 0;
      return <>
        {super.getSelectionButtons(selection)}
        <IconButton icon={'drive_file_move'} title={hasImages ? 'Move all images to another cluster (existing or new)' : 'Cluster has no images to move'}
                    disabled={!hasImages}
                    onClick={() => this.openMoveClusterModal(cluster)}>
        </IconButton>
        {findReferencesBtn}
      </>
    }
    return <>
      {super.getSelectionButtons(selection)}
      {findReferencesBtn}
    </>;
  }
}

// For production
// export default ImgClustersManager;

// For development
export default function IMClusterLiveReload(props) {
  return <ImgClustersManager {...props} />
}
